import { CurrencyPipe, DOCUMENT, DatePipe, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
    AfterContentInit,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    Signal,
    TemplateRef,
    ViewChild,
    booleanAttribute,
    computed,
    inject,
    input
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule, MatSelectionListChange } from '@angular/material/list';
import { MatMenuModule, MatMenuPanel, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggle, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
    CellClickEvent,
    ColumnReorderEvent,
    DataStateChangeEvent,
    DetailCollapseEvent,
    DetailExpandEvent,
    ExcelExportEvent,
    ExcelModule,
    FilterService,
    FilterableSettings,
    GridDataResult,
    GridModule,
    GridComponent as KendoGridComponent,
    PDFModule,
    PagerSettings,
    RowArgs,
    RowClassArgs,
    ScrollMode,
    SelectableSettings,
    SelectionEvent
} from '@progress/kendo-angular-grid';
import { SVGIcon, SVGIconModule } from '@progress/kendo-angular-icons';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import {
    CompositeFilterDescriptor,
    FilterDescriptor,
    GroupDescriptor,
    GroupResult,
    SortDescriptor
} from '@progress/kendo-data-query';
import { fileCsvIcon, fileExcelIcon, filePdfIcon } from '@progress/kendo-svg-icons';
import { cloneDeep, isEqual } from 'es-toolkit/compat';
import { Subject } from 'rxjs';
import { DocumentTypeLevel } from 'src/app/compliance/enums/document-type-level.enum';
import { CheckboxFilterComponent } from 'src/app/shared/grid/checkbox-filter.component';
import { DateRangeFilterComponent } from 'src/app/shared/grid/date-range-filter.component';
import { DropDownFilterRowComponent } from 'src/app/shared/grid/dropdown-filter-row.component';
import { ToolbarMenuItem } from 'src/app/shared/grid/enum/toolbar-menu-item.const';
import { GridOptionAutoCompleteMultiselectFilter } from 'src/app/shared/grid/filters/grid-option-autocomplete-multiselect-filter.component';
import { GridComponentService } from 'src/app/shared/grid/grid-component.service';
import { SpecialPermissions, toSpecialPermissions } from 'src/app/shared/grid/models/special-permissions.model';
import { MultiSelectFilterRowComponent } from 'src/app/shared/grid/multiselect-filter-row.component';
import { TextFilterComponent } from 'src/app/shared/grid/text-filter.component';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { GridColumnStateModel } from 'src/app/shared/models/grid-column-state.model';
import { BooleanPipe } from 'src/app/shared/pipes/boolean.pipe';
import { NovaMatIconRegistryService } from 'src/app/shared/services/mat-icon-registry.service';
import { FetchArgs, PagingTokenFixed, filterToObject, flatten, objectToFilters } from '../models/core';
import { MicDropFeatureFlags } from '../models/enums/team-feature-flags/mic-drop-feature-flags.enum';
import { DefaultRowSelectedAccessorDirective } from './default-row-selected-accessor.directive';
import { GridColumnDirective } from './grid-column.directive';
import { GridToggleDirective } from './grid-toggle.directive';

export type ToolbarMenuItem = (typeof ToolbarMenuItem)[keyof typeof ToolbarMenuItem];

export const MAX_PAGE_SIZE = 1000000;
const EMPTY_FILTER = {
    logic: 'and',
    filters: []
} as CompositeFilterDescriptor;

@UntilDestroy()
@Component({
    selector: 'nova-grid',
    templateUrl: './grid.component.html',
    styleUrls: ['./grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        GridModule,
        MatIconModule,
        MatSlideToggleModule,
        MatButtonModule,
        MatMenuModule,
        TooltipModule,
        MultiSelectFilterRowComponent,
        CheckboxFilterComponent,
        TextFilterComponent,
        GridOptionAutoCompleteMultiselectFilter,
        DropDownFilterRowComponent,
        DateRangeFilterComponent,
        NgTemplateOutlet,
        PDFModule,
        ExcelModule,
        MatListModule,
        SVGIconModule,
        MatProgressSpinnerModule,
        NgStyle,
        FormsModule,
        MatSlideToggleModule,
        MatTooltipModule,
        BooleanPipe,
        CurrencyPipe,
        DatePipe,
        DefaultRowSelectedAccessorDirective
    ]
})
export class GridComponent implements OnInit, AfterViewInit, AfterContentInit, OnDestroy {
    readonly gridService: GridComponentService = inject(GridComponentService);
    private readonly el: ElementRef<HTMLElement> = inject(ElementRef);
    public ref: ChangeDetectorRef = inject(ChangeDetectorRef);
    private readonly _document: Document = inject(DOCUMENT);
    private readonly _novaMatIconRegistry: NovaMatIconRegistryService = inject(NovaMatIconRegistryService);

    ToolbarMenuItem = ToolbarMenuItem;
    toolbarMenuItems = [
        {
            label: ToolbarMenuItem.ShowHideColumns,
            icon: 'visibility',
            subMenu: true,
            visible: true,
            dataCy: 'sharedGridColumnPickerMenuButton'
        },
        {
            label: ToolbarMenuItem.SaveColumns,
            icon: 'save',
            action: () => this.saveGridState(),
            visible: true
        },
        {
            label: ToolbarMenuItem.ResetColumns,
            icon: 'restore',
            action: () => this.resetGridState(),
            visible: true
        },
        {
            label: ToolbarMenuItem.SaveFilters,
            icon: 'tune',
            action: () => this.saveFiltersState(),
            visible: false
        },
        {
            label: ToolbarMenuItem.ResetFilters,
            icon: 'reset_settings',
            action: () => this.resetFiltersState(),
            visible: false
        },
        {
            label: ToolbarMenuItem.WfdSaveFilter,
            icon: 'bookmark_add',
            action: () => this.saveFiltersState(),
            visible: false,
            disabled: true,
            dataCy: 'wfdSaveFilterButton'
        },
        {
            label: ToolbarMenuItem.LoadFilters,
            icon: 'bookmarks-border',
            subMenu: true,
            visible: false,
            dataCy: 'loadFiltersPickerMenuButton'
        }
    ];

    specialPermissions = input(
        {
            canReassignCS: false,
            canUnassignCS: false,
            canReassignCL: false,
            canUnassignCL: false,
            canAddQm: false,
            canEditQualificationMapping: false,
            canAssignQm: false,
            canBulkSetAutMatchSettings: false,
            canBulkSetQmAutMatchSettings: false,
            canBulkEditACStatus: false,
            canEditTravelGlobal: false,
            canEditTravelAssociation: false,
            canEditLocumsGlobal: false,
            canEditLocumsAssociation: false,
            canEditAccessControlGlobal: false,
            canEditAccessControlAssociation: false,
            canEditTravelFacility: false,
            canEditLocumsFacility: false,
            canEditAccessControlFacility: false
        },
        { transform: toSpecialPermissions }
    );

    @Input() defaultCols: GridColumnStateModel[] = [];
    @Input() gridId = '';
    @Input() canExportToExcel = false;
    @Input() canExportToPdf = false;
    @Input() canExportToCSV = false;
    @Input() directExportToExcel = false;
    @Input() scrollable: ScrollMode = 'scrollable'; //('none' | 'scrollable' | 'virtual') - Kendo default is 'scrollable'
    @Input() style = null;
    @Input() pageable: boolean | PagerSettings = {
        info: true,
        type: 'input',
        pageSizes: [10, 25, 50, 100],
        previousNext: true
    };

    @Input() isLoading = false;
    @Input() filterable: FilterableSettings = true;
    @Input() columnMenu = false;
    @Input() name = '';
    @Input() resizable = true;
    @Input() autoFitColumns = true;
    @Input() reorderable = true;
    @Input() singleSelection = false;
    @Input() selectable: SelectableSettings = { enabled: false, mode: 'single' };
    @Input() needDetailView = false;
    @Input() needToolbar = true;
    @Input() needRefresh = true;
    @Input() needToolbarColumnShowHideMenu = true;
    @Input() needExpandCollapseButtons = false;
    @Input() enableDisableCollapseButtons = false;
    @Input() showExpandButton = true;
    @Input() showCollapseButton = true;
    @Input() showClearFilterButton = false;
    @Input() detailViewTemplate!: TemplateRef<unknown>;
    @Input() toolbarTemplate!: TemplateRef<unknown>;
    @Input() sendJobsTemplate!: TemplateRef<unknown>;
    @Input() defaultFetchArgs: FetchArgs = { skip: 0, pageSize: 50, filter: {} };
    @Input() excelOutputFileName = 'results.xlsx';
    @Input() noRecordsMessage = 'No records available.';
    @Input() showClientPortalVisibility = false;
    @Input() showNewRequirement = false;

    @Output() dataNeeded = new EventEmitter<any>();
    @Output() columnVisibilityToggled = new EventEmitter<GridColumnDirective>();
    @Output() rowSelected = new EventEmitter<any[]>();
    @Output() csReassign = new EventEmitter<any[]>();
    @Output() csUnassign = new EventEmitter<any[]>();
    @Output() clReassign = new EventEmitter<any[]>();
    @Output() clUnassign = new EventEmitter<any[]>();
    @Output() addQmAssociation = new EventEmitter<unknown>();
    @Output() assignQmAssociation = new EventEmitter<any[]>();
    @Output() editBulkAutomatchSettings = new EventEmitter<any[]>();
    @Output() editBulkQmAutomatchSettings = new EventEmitter<any[]>();
    @Output() bulkQualificationMappingStatus = new EventEmitter<any[]>();
    @Output() bulkEditACStatus = new EventEmitter<any[]>();
    @Output() createNewFacilityRequirement = new EventEmitter<void>();
    @Output() createNewTravelRequirement = new EventEmitter<void>();
    @Output() createNewLocumsRequirement = new EventEmitter<void>();
    @Output() createNewAccessControlRequirement = new EventEmitter<void>();
    @Output() viewReqList = new EventEmitter<void>();
    @Output() rowDeselected = new EventEmitter<unknown>();
    @Output() columnsReset = new EventEmitter();
    @Output() columnsSave = new EventEmitter();
    @Output() filterSave = new EventEmitter<void>();
    @Output() filterReset = new EventEmitter<void>();
    @Output() filterClear = new EventEmitter<void>();
    @Output() columnResize = new EventEmitter();
    @Output() dataStateChange = new EventEmitter<DataStateChangeEvent>(); //TODO - replace this with filterChanged()
    @Output() filterChanged = new EventEmitter<unknown>();
    @Output() excelExport = new EventEmitter<ExcelExportEvent>();
    @Output() columnReorder = new EventEmitter<ColumnReorderEvent>();
    @Output() selectAll = new EventEmitter<string>();
    @Output('cellClicked') cellClickedEmitter = new EventEmitter<CellClickEvent>();
    @Output() dataNeededExport = new EventEmitter<any>();
    @Output() editClientPortalVisibility = new EventEmitter<void>();
    @Input() hasGridMenu = false;
    @Input() useBulkActionMenu = false;
    @Input() gridMenu!: MatMenuPanel | null;
    @Input() group: GroupDescriptor[] = [];
    @Input() groupable = false;
    @Input() selectionKey = '';
    @Input() clearRowSelectionOnLoad = false;
    headerCheckboxSelector = input<string>('headerCheckbox');

    @Output() onDetailExpand = new EventEmitter<DetailExpandEvent>();
    @Output() onDetailCollapse = new EventEmitter<DetailCollapseEvent>();
    @Output() onDetailExpandAll = new EventEmitter<void>();
    // properties that are initialized with the DOM
    @ViewChild(KendoGridComponent) grid!: KendoGridComponent;
    @ContentChildren(GridColumnDirective) columns!: QueryList<GridColumnDirective>;
    @ContentChildren(GridToggleDirective) toggles!: QueryList<GridToggleDirective>;
    @ViewChild(KendoGridComponent, { read: FilterService }) filterService!: FilterService;
    @HostBinding('class.scrollable') isHostElementScrollable = true;
    @ViewChild('toggleElement') matSlideToggle!: MatSlideToggle;
    // This callback is needed to supply the id or key of the master record.
    @Input() useNewExpandCollapse = false;
    @Input() exportSelecetdRows = false;
    @Input() directExportSelectedRowsToExcel = false;

    @Input() selectableDisabledResolver: (val: unknown) => boolean = () => false;
    @Input() selectHintResolver: (val: unknown) => string = () => '';
    @Input() rowClassCallback: (row: RowClassArgs) => unknown = () => null;
    @Input() selectableResolver: (val: unknown) => boolean = () => true;
    @Input() expandDetailsByCallback: (dataItem: any) => unknown = () => null;
    private _overrideToolbarMenuVisibility: Partial<Record<ToolbarMenuItem, boolean>> = {};

    private readonly timeoutIds = new Map<string, ReturnType<typeof setTimeout>>();

    @Input()
    set overrideToolbarMenuVisibility(value: Partial<Record<ToolbarMenuItem, boolean>>) {
        this._overrideToolbarMenuVisibility = value;
        this.updateVisibility(); // The same logic you had in ngOnChanges
    }

    get overrideToolbarMenuVisibility(): Partial<Record<ToolbarMenuItem, boolean>> {
        return this._overrideToolbarMenuVisibility;
    }

    @Input({ transform: booleanAttribute }) persistSelection = false;

    // Saved Filter Submenu for overrideToolbarMenuVisibility
    @Input() savedFiltersMenuTemplate!: TemplateRef<unknown>;

    @Input() requirementLevel = DocumentTypeLevel.Global;

    @ViewChild('toolbarColumnMenuTrigger') toolbarColumnMenuTrigger!: MatMenuTrigger;

    // The array where Kendo handles the data ids or keys when expanding/collapsing
    // a master/detail record. Adding keys to this array will expand the row.
    // Removing the id or key, will collapse the row.
    public expandedDetailKeys: any[] = [];

    get BaseGridMaxPageSize() {
        return this.maxRecords;
    }

    pdfFileIcon: SVGIcon = filePdfIcon;
    excelFileIcon: SVGIcon = fileExcelIcon;
    csvFileIcon: SVGIcon = fileCsvIcon;
    filter = EMPTY_FILTER;
    selectedRows: any[] = [];
    sort: SortDescriptor[] = [];
    isAllExpanded = false;

    @Input() maxRecords = MAX_PAGE_SIZE;
    showExportToolTip = false;
    onSaveColumns = new Subject<void>();
    onSaveFilters = new Subject<void>();
    onResetColumns = new Subject<void>();
    useUnpagedLoadingSpinner = false;
    canAddQmAssociation = false;
    canAssignQmAssociation = false;
    showBulkEditStatusButton = false;
    documentTypeLevel = DocumentTypeLevel;
    showFacilitySpecificButtons: Signal<boolean> = computed(() => {
        if (this.requirementLevel !== DocumentTypeLevel.Facility) {
            return false;
        }

        return (
            this.specialPermissions().canEditTravelFacility ||
            this.specialPermissions().canEditLocumsFacility ||
            this.specialPermissions().canEditAccessControlFacility
        );
    });

    showNewRequirementsButtons: Signal<boolean> = computed(() => {
        if (this.showNewRequirement === false) {
            return false;
        }

        if (this.requirementLevel === DocumentTypeLevel.Facility) {
            return false;
        }

        return this.hasAppropriatePermissionsForLevel(this.specialPermissions(), this.requirementLevel);
    });

    showClientPortalVisibilityButton: Signal<boolean> = computed(() => {
        if (this.showClientPortalVisibility === false) {
            return false;
        }

        if (this.requirementLevel === DocumentTypeLevel.State) {
            return false;
        }

        return this.hasAppropriatePermissionsForLevel(this.specialPermissions(), this.requirementLevel);
    });

    canCreateNewTravelRequirement: Signal<boolean> = computed(() => {
        return this.hasAppropriateTravelPermissionsForLevel(this.specialPermissions(), this.requirementLevel);
    });

    canCreateNewLocumsRequirement: Signal<boolean> = computed(() => {
        return this.hasAppropriateLocumsPermissionsForLevel(this.specialPermissions(), this.requirementLevel);
    });

    canCreateNewAccessControlRequirement: Signal<boolean> = computed(() => {
        return this.hasAppropriateAccessControlPermissionsForLevel(this.specialPermissions(), this.requirementLevel);
    });

    includeHiddenColumnsInExcel = false;
    protected selectedKeys = new Array<any>();
    readonly featureFlag = FeatureFlag;

    readonly micDropFeatureFlag = MicDropFeatureFlags;

    private dataState: 'exportToPdf' | 'exportToExcel' | 'display' | 'exportToCSV' = 'display';
    private readonly STYLE_KEYS = [
        'background',
        'color',
        'bold',
        'italic',
        'underline',
        'fontName',
        'fontSize',
        'textAlign',
        'verticalAlign',
        'wrap',
        'border',
        'format'
    ];

    initialColumnState: GridColumnStateModel[] = [];
    private _args: FetchArgs = { skip: 0, pageSize: 50, filter: {} };
    showEditClientPortalVisibilityButton: Signal<boolean> = computed(() => {
        return this.hasAppropriatePermissionsForLevel(this.specialPermissions(), this.requirementLevel);
    });

    @ViewChild(DefaultRowSelectedAccessorDirective) defaultRowSelectedAccessor!: DefaultRowSelectedAccessorDirective;

    constructor() {
        this._novaMatIconRegistry.addSvgIcon('reset_settings', 'assets/images/svg/reset_settings.svg');
        this.isRowSelected = this.isRowSelected.bind(this);
    }

    @Input() set maxPageSize(value: number) {
        if (value) {
            this.maxRecords = value;
            this.showExportToolTip = true;
        }
    }

    get args(): FetchArgs {
        return this._args;
    }

    @Input() set args(value: FetchArgs) {
        this.setArgs(value);
    }

    // input/output properties
    get data(): GridDataResult {
        return !this.grid ? { data: [], total: 0 } : (this.grid.data as GridDataResult);
    }

    @Input() set data(value: GridDataResult | any[]) {
        this.setData(value);
    }

    @Input() set pagingToken(value: PagingTokenFixed<any>) {
        this.setData(value ? { data: value.data, total: value.filtered } : { data: [], total: 0 });
    }

    expandDetailsBy = (dataItem: any): any => {
        return this.expandDetailsByCallback ? this.expandDetailsByCallback(dataItem) : null;
    };

    ngAfterViewInit(): void {
        this.initialColumnState = this.getColumnState();
        this.setTimeoutById('loadGridState', () => this.loadGridState(), 0);
    }

    ngAfterContentInit(): void {
        // Wait for the query list to resolve before loading state
        this.toggles.changes.pipe(untilDestroyed(this)).subscribe(() => {
            this._loadToggleState();
        });
    }

    ngOnInit(): void {
        this.isHostElementScrollable = this.scrollable === 'scrollable';
        this.showBulkEditStatusButton = true;
    }

    ngOnDestroy(): void {
        this.clearAllTimeouts();
    }

    private updateVisibility(): void {
        this.toolbarMenuItems = this.toolbarMenuItems.map((item) => ({
            ...item,
            visible: this.overrideToolbarMenuVisibility[item.label] ?? item.visible
        }));
    }

    private clearTimeoutById(key: string): void {
        const timeoutId = this.timeoutIds.get(key);
        if (timeoutId) {
            clearTimeout(timeoutId);
            this.timeoutIds.delete(key);
        }
    }

    private setTimeoutById(key: string, callback: () => void, delay: number): void {
        // Clear existing timeout with this key
        this.clearTimeoutById(key);

        // Set new timeout
        const timeoutId = setTimeout(() => {
            callback();
            this.timeoutIds.delete(key); // Auto-cleanup after execution
        }, delay);

        this.timeoutIds.set(key, timeoutId);
    }

    private clearAllTimeouts(): void {
        this.timeoutIds.forEach((timeoutId) => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        });
        this.timeoutIds.clear();
    }

    updateWfdSaveFilterToolbarState(isDisabled: boolean): void {
        this.toolbarMenuItems.forEach((item) => {
            if (item.label === ToolbarMenuItem.WfdSaveFilter) {
                item.disabled = isDisabled;
            }
        });
    }

    clearFilter(filterName: string | undefined = undefined): void {
        if (filterName) {
            delete this.args.filter[filterName];
            this.clearSingleFilter(this.filter, filterName);
        } else {
            this.args.filter = {};
            this.filter = EMPTY_FILTER;
        }
    }

    clearSingleFilter(filterObject: FilterDescriptor | CompositeFilterDescriptor, fieldName: string): boolean {
        if (filterObject.hasOwnProperty('field')) {
            const descriptor = filterObject as FilterDescriptor;
            if (descriptor.field === fieldName) {
                descriptor.value = null;
                return true;
            }
        } else if (filterObject.hasOwnProperty('filters')) {
            const filters = (filterObject as CompositeFilterDescriptor).filters;
            filters.forEach((x, i) => {
                if (this.clearSingleFilter(x, fieldName)) {
                    filters.splice(i, 1);
                    return false;
                }
            });
        }
        return false;
    }

    clearAllData(): void {
        const value = { data: [], total: 0 };
        this.grid.data = value;
        this.clearFilter();
        this.sort = [];
    }

    /**
     * This sorts the columns by "title" in ascending order.
     */
    getSortedColumns(): GridColumnDirective[] {
        const columnsArray = this.columns.toArray();
        return columnsArray.sort((a, b) => (a.title > b.title ? 1 : -1));
    }

    emitSelection(event: SelectionEvent): void {
        // Removed for issues encountered with Kendo v17 with the Angular v17 upgrade
        // if (!event.ctrlKey && this.selectable.mode === 'multiple') {
        //     return;
        // }

        if (event?.selectedRows && event.selectedRows.length > 0) {
            this.updateSelectedRows(event.selectedRows);
        }

        if (event?.deselectedRows && event.deselectedRows.length > 0) {
            this.selectedRows = this.selectedRows.filter((item) => {
                if (event.deselectedRows) {
                    return !event.deselectedRows.some((row) =>
                        this.selectionKey && this.selectionKey.length > 0
                            ? row.dataItem[this.selectionKey] === item[this.selectionKey]
                            : row.dataItem === item
                    );
                } else {
                    return false;
                }
            });

            this.rowDeselected.emit(event.deselectedRows);
        }

        if (this.selectableResolver) {
            this.selectedRows = this.selectedRows.filter(this.selectableResolver);
        }

        this.rowSelected.emit(this.selectedRows);
    }

    updateSelectedRows(rows: RowArgs[]) {
        if (typeof this.selectable === 'object') {
            const s = this.selectable;
            if (s.mode === 'single') {
                this.selectedRows.push(rows[0].dataItem);
            } else {
                this.selectedRows.push(...rows.map((row) => row.dataItem));
            }
        } else {
            this.selectedRows.push(rows[0].dataItem);
        }
    }

    isGridFilterable(column: GridColumnDirective): boolean {
        if (this.isFieldToggleEnabled(column.field) || !column.filterable) {
            return false;
        }

        if (column.filterType !== 'none' || column.filterTemplate || column.filterCellTemplate) {
            return true;
        } else {
            return false;
        }
    }

    exportAllData(type: 'exportToExcel' | 'exportToPdf' | 'exportToCSV', includeHiddenColumnsInExcel = false): void {
        this.dataState = type;
        this.exportSelecetdRows = false;
        this.includeHiddenColumnsInExcel = includeHiddenColumnsInExcel;
        this.dataNeeded.emit({ skip: 0, pageSize: this.maxRecords, filter: this.args.filter, sort: this.args.sort });
    }

    exportSelectedData(type: 'exportToExcel' | 'exportToPdf' | 'exportToCSV'): void {
        this.dataState = type;
        this.exportSelecetdRows = true;
        this.dataNeeded.emit({ skip: 0, pageSize: this.maxRecords, filter: this.args.filter, sort: this.args.sort });
    }

    onCSReassignClicked(): void {
        this.csReassign.emit(this.selectedRows);
    }

    onCSUnassignClicked(): void {
        this.csUnassign.emit(this.selectedRows);
    }

    onAddQmClicked(): void {
        this.addQmAssociation.emit();
    }

    onAssignQmClicked(): void {
        this.assignQmAssociation.emit(this.selectedRows);
    }

    onEditAutomatchSettingsClicked(): void {
        this.editBulkAutomatchSettings.emit(this.selectedRows);
    }

    onEditQmAutomatchSettingsClicked(): void {
        this.editBulkQmAutomatchSettings.emit(this.selectedRows);
    }

    onBulkEditQualificationMapping(): void {
        this.bulkQualificationMappingStatus.emit(this.selectedRows);
    }

    onBulkEditACStatus(): void {
        this.bulkEditACStatus.emit(this.selectedRows);
    }

    onCLReassignClicked(): void {
        this.clReassign.emit(this.selectedRows);
    }

    onCLUnassignClicked(): void {
        this.clUnassign.emit(this.selectedRows);
    }

    onCreateNewFacilityRequirement() {
        this.createNewFacilityRequirement.emit();
    }

    onCreateNewTravelRequirement() {
        this.createNewTravelRequirement.emit();
    }

    onCreateNewLocumsRequirement() {
        this.createNewLocumsRequirement.emit();
    }

    onCreateNewAccessControlRequirement() {
        this.createNewAccessControlRequirement.emit();
    }

    onEditClientPortalVisibilityClicked(): void {
        this.editClientPortalVisibility.emit();
    }

    onViewReqList(): void {
        this.viewReqList.emit();
    }

    isRowSelected(e: RowArgs | undefined): boolean {
        if (!!this.persistSelection && !!this.defaultRowSelectedAccessor) {
            return e ? this.defaultRowSelectedAccessor.defaultRowSelected(e) : false;
        }

        if (this.selectionKey && this.selectionKey.length > 0) {
            return this.selectedRows.find((a) => a[this.selectionKey] === e?.dataItem[this.selectionKey]) !== undefined;
        }

        return this.selectedRows.find((a) => a === e?.dataItem) !== undefined;
    }

    formatExcelData(e: ExcelExportEvent): void {
        if (this.directExportSelectedRowsToExcel && this.selectedRows?.length === 0 && this.exportSelecetdRows) {
            return;
        }

        let rows: unknown[] = e.workbook.sheets[0].rows;

        const dataResult = this.grid.data as GridDataResult;

        let dataResultData: unknown[] = [];

        if (dataResult?.data && dataResult.data.length === rows.length - 1) {
            dataResultData = dataResult.data;
        }

        if (this.directExportSelectedRowsToExcel && this.selectedRows?.length > 0 && this.exportSelecetdRows) {
            const selectedExcelRow: unknown[] = [];

            rows.forEach((row: any, rowIndex: number) => {
                if (row.type === 'header') {
                    selectedExcelRow.push(rows[rowIndex]);
                } else {
                    let data: RowArgs | undefined;

                    if (dataResultData) {
                        data = { dataItem: dataResultData[rowIndex - 1], index: rowIndex - 1 };
                    }

                    if (this.isRowSelected(data)) {
                        selectedExcelRow.push(rows[rowIndex]);
                    }
                }
            });

            rows = selectedExcelRow;
            e.workbook.sheets[0].rows = rows;
            dataResultData = this.selectedRows;
        }

        const colExports: Array<{ index: number; export: (data: any, dataItem: any) => string | void }> = [];

        rows.forEach((row: any, rowIndex: number) => {
            let col: GridColumnDirective | undefined;

            if (row.type === 'header') {
                // Need to collect the column exported functions attached to the column
                // Since the columns can be dragged or removed this needs to be re-run every export
                row.cells.forEach((cell: any, index: number) => {
                    col = this.columns.find((foundCol) => foundCol.title === cell.value);

                    if (col?.columnExported) {
                        colExports.push({
                            index,
                            export: col.columnExported
                        });
                    }
                });
            } else if (row.type === 'data') {
                let data: unknown = null;

                if (dataResultData) {
                    data = dataResultData[rowIndex - 1];
                }

                // Export the cell and format it if the column has a custom export method
                row.cells.forEach((cell: any, index: number) => {
                    const columnExport:
                        | { index: number; export: (data: any, dataItem: any) => string | void }
                        | undefined = colExports.find((colExport: any) => colExport.index === index);

                    if (columnExport) {
                        cell.value = columnExport.export(cell.value, data);
                    }
                });
            }
        });

        if (this.includeHiddenColumnsInExcel) {
            this.appendHiddenColumnsWithHeaderStyle(e, dataResultData);
        }

        this.excelExport.emit(e);
    }

    /** Append hidden columns with the same header style as unhidden columns. */
    appendHiddenColumnsWithHeaderStyle(e: ExcelExportEvent, dataResultData: any[]): void {
        const sheet = e.workbook.sheets[0];
        const rows: any[] = sheet.rows as any[];
        const header = rows.find((r) => r.type === 'header');
        if (!header) {
            return;
        }

        const hiddenCols = this.columns.filter((c) => c.hidden);
        if (!hiddenCols.length) {
            return;
        }

        const headerStyle = this.applyHeaderStyle(header);
        sheet.columns = sheet.columns || [];

        // build headers
        hiddenCols.forEach((c) => {
            header.cells.push({
                value: c.title ?? c.field ?? '',
                ...headerStyle
            });

            const colWidth = (c.width && Number(c.width)) || 100;
            sheet.columns.push({ width: colWidth });
        });

        // build data rows
        rows.forEach((row: any, rowIndex: number) => {
            if (row.type !== 'data') {
                return;
            }
            const item = dataResultData ? dataResultData[rowIndex - 1] : null;

            hiddenCols.forEach((c) => {
                let value = item ? item[c.field] : null;
                if (c.columnExported) {
                    value = c.columnExported(value, item);
                }
                row.cells.push({ value });
            });
        });
    }

    /** Apply the style from the first existing header cell to reuse. */
    applyHeaderStyle(headerRow: any) {
        if (!headerRow || !Array.isArray(headerRow.cells)) {
            return undefined;
        }

        // Find the first non-null cell
        const ref = headerRow.cells.find((cell: any) => cell != null);
        if (!ref) {
            return undefined;
        }

        const styleObj: Record<string, string> = {};

        for (const key of this.STYLE_KEYS) {
            const value = ref[key];

            if (value !== undefined) {
                styleObj[key] = typeof value === 'object' ? JSON.parse(JSON.stringify(value)) : value;
            }
        }

        return Object.keys(styleObj).length > 0 ? styleObj : undefined;
    }

    collapseRow(index: number) {
        // Don't use this kendo grid method. This is old method intended for legacy apps.
        // << this.grid.collapseRow(index) >>
        if (this.data?.data?.length > index) {
            this.collapseRowData(this.data.data[index]);
        }
    }

    collapseRowData(dataId: any) {
        const foundAt = this.expandedDetailKeys.findIndex((val, index) => {
            return dataId === val ? index : -1;
        });
        if (foundAt !== -1) {
            this.expandedDetailKeys.splice(foundAt, 1);
        }
        this.isAllExpanded = false;
    }

    collapseGroup(index: string) {
        this.grid.collapseGroup(index);
    }

    expandGroup(index: string) {
        this.grid.expandGroup(index);
    }

    expandRow(index: number) {
        // Don't use this kendo grid method. This is old method intended for legacy apps.
        // << this.grid.expandRow(index) >>
        const data = this.data;
        if (data.data.length > 0 && data.data.length < index) {
            this.expandRowData(this.data.data[index]);
        }
        this.isAllExpanded = data.data.length >= this.expandedDetailKeys.length;
    }

    expandRowData(dataId: any) {
        if (!this.expandedDetailKeys.includes(dataId)) {
            this.expandedDetailKeys.push(dataId);
        }

        //Replace with this when switching to RXJS V8
        //if (!this.onDetailExpand.observed) {
        //DO NOT REPEAT THIS USAGE observers will be deprecated in RXJS 8
        if (!this.onDetailExpand.observed) {
            return;
        }

        const index = this.data?.data?.findIndex((e) => this.expandDetailsBy(e) === dataId);

        if (index !== undefined && index !== -1) {
            this.onDetailExpand.emit({ dataItem: this.data.data[index], index } as DetailExpandEvent);
        }
    }

    load(event: DataStateChangeEvent): void {
        this.dataStateChange.emit(event);

        if (event.filter) {
            this.filter = event.filter;
            const filteredObj = filterToObject(event.filter);
            this.args = { ...this.args, filter: filteredObj };

            this.filterChanged.emit(this.args.filter);

            if (this.clearRowSelectionOnLoad) {
                this.clearRowSelection();
            }
        }

        if (event.sort) {
            this.sort = event.sort;
            this.args = { ...this.args, sort: event.sort[0] };
        }

        this.args = {
            ...this.args,
            skip: event.skip,
            pageSize: event.take
        };
        this.dataNeeded.emit(this.args);
    }

    saveGridState(): void {
        this.gridService.saveColumns(this.getName(), this.getColumnState());
        const saveFiltersItem = this.toolbarMenuItems.find((item) => item.label === ToolbarMenuItem.SaveFilters);
        if (!saveFiltersItem || !saveFiltersItem.visible) {
            this.gridService.saveFetchArgs(this.getName(), this._args);
        }
        this.columnsSave.emit();
        this.onSaveColumns.next();
    }

    saveFiltersState(): void {
        this.gridService.saveFetchArgs(this.getName(), this._args);
        this.filterSave.emit();
        this.onSaveFilters.next();
    }

    resetFiltersState(): void {
        this.gridService.clearFetchArgsState(this.getName());
        this.filterReset.emit();
    }

    reload(persistPage = false): void {
        if (!persistPage) {
            this.args = { ...this.args, skip: 0 };
        }

        this.dataNeeded.emit(this.args);
        this.clearRowSelection();
    }

    clearRowSelection(): void {
        this.selectedRows = [];
        this.selectedKeys = [];
        this._setHeaderCheckboxState(false, false);
        this.rowSelected.emit(this.selectedRows);
    }

    resetGridState(removeColsFromLocalStorage = true): void {
        const saveFiltersMenuItem = this.toolbarMenuItems.find((item) => item.label === ToolbarMenuItem.SaveFilters);
        if (!saveFiltersMenuItem || !saveFiltersMenuItem.visible) {
            this.resetFetchArgs();
        }
        this.resetColumns(removeColsFromLocalStorage);
        this.resetAsyncFilters();

        // Comment this out for now
        // as when we reset we should not save again
        // It should be a clean reset
        // this.saveGridState(true);
    }

    clearGridState(): void {
        const clearArgs: FetchArgs = {
            filter: {},
            sort: {
                field: '',
                dir: 'asc'
            },
            skip: 0,
            pageSize: 50
        };
        this.resetFetchArgs(clearArgs);
        this.filterClear.emit();

        // Comment this out for now
        // as when we reset we shouldn't save again
        // It should be a full reset.
        // this.saveGridState(true);
    }

    resetColumns(removeColsFromLocalStorage: boolean = true): void {
        if (removeColsFromLocalStorage === true) {
            this.gridService.clearColumnsState(this.getName());
        }
        if (this.initialColumnState) {
            this.setColumnState(this.initialColumnState);
        }
        if (this.resizable && this.autoFitColumns) {
            this.grid.autoFitColumns();
        }

        this._setHeaderCheckboxState(false, false);

        this.setDefaultColumns();
        this.columnsReset.emit();
        this.onResetColumns.next();
    }

    resetAsyncFilters(): void {
        this.gridService.clearAsyncFilters(this.getName());
    }

    setDefaultColumns() {
        if (this.defaultCols) {
            this.loadGridState(this.defaultCols);
            this.clearRowSelection();
        }
    }

    resetFetchArgs(newArgs: FetchArgs | undefined = undefined): void {
        this.toggles?.forEach((t) => (t.checked = false));
        this.gridService.clearFetchArgsState(this.getName());
        this.sort = [];
        this.filter = EMPTY_FILTER;
        const defaultArgs = cloneDeep(this.defaultFetchArgs);
        this.setArgs(newArgs || defaultArgs, true);
        this.filterService?.changes.next(this.filter);
    }

    loadGridState(defaults: GridColumnStateModel[] = [], updateColumnVisibility: boolean = false): void {
        let colStates = this.gridService.getColumns(this.getName()) ?? [];

        if (colStates || defaults) {
            if (defaults) {
                if (!colStates) {
                    colStates = [];
                }
                defaults.forEach((item: GridColumnStateModel) => {
                    const col = this.findGridColumnState(item, colStates);
                    if (!col) {
                        colStates.push(item);
                    }

                    if (updateColumnVisibility === true) {
                        let colStateItem = null;

                        colStateItem = this.findGridColumnState(item, colStates);

                        if (colStateItem) {
                            if (item.hidden === false) {
                                colStateItem.hidden = false;
                            }
                        }
                    }
                });
            }
            this.setColumnState(colStates);
        }
    }

    getGridColumnsForSave(): GridColumnStateModel[] {
        const savedColStates = this.gridService.getColumns(this.getName());

        const newColStates = this.getColumnState();
        if (savedColStates && newColStates) {
            newColStates.forEach((item: GridColumnStateModel) => {
                const savedColStatesItem = this.findGridColumnState(item, savedColStates) as GridColumnStateModel;
                if (item.hidden === false) {
                    savedColStatesItem.hidden = false;
                    savedColStatesItem.order = item.order;
                    savedColStatesItem.width = item.width;
                }
            });
            return savedColStates;
        } else {
            return newColStates;
        }
    }

    toggleVisibility(event: MatSelectionListChange): void {
        const column = event.options[0].value as GridColumnDirective;
        column.hidden = !event.options[0].selected;
        this.setTimeoutById(
            'columnVisibilityToggled',
            () => {
                this.columnVisibilityToggled.emit(column);
            },
            100
        );
    }

    onCellClicked(arg: CellClickEvent): void {
        this.cellClickedEmitter.emit(arg);
    }

    onColumnResize(): void {
        this.setTimeoutById(
            'columnResize',
            () => {
                this.columnResize.emit();
            },
            100
        );
    }

    onColumnReordered(event: ColumnReorderEvent): void {
        this.setTimeoutById(
            'columnReorder',
            () => {
                this.columnReorder.emit(event);
            },
            0
        );
    }

    onPageChanged(): void {
        if (!this.persistSelection) {
            this.clearRowSelection();
        }
    }

    individualItemChange(): void {
        const data = Array.isArray(this.data) ? this.data : this.data?.data;
        let selectableItems = data.filter(this.selectableResolver);
        const isNestedItems = data.every((item) => !!Object.getOwnPropertyDescriptor(item, 'items'));

        // Need to handle rows that are grouped
        if (isNestedItems) {
            const rows = data.map((item) => item.items);
            selectableItems = flatten(rows).filter(this.selectableResolver);
        }

        if (this.selectedRows.length) {
            if (this.selectedRows.length !== selectableItems.length) {
                this._setHeaderCheckboxState(true, false);
            } else {
                this._setHeaderCheckboxState(false, true);
            }
        } else {
            this._setHeaderCheckboxState(false, false);
        }

        this.rowSelected.emit(this.selectedRows);
    }

    onSelectAll(event: Event) {
        const input = event.target as HTMLInputElement;

        const data = Array.isArray(this.data) ? this.data : this.data?.data;
        // Check if the grid is using group headers and rows
        const isNestedItems = data.every((item) => !!Object.getOwnPropertyDescriptor(item, 'items')); // item.hasOwnProperty('items'));

        if (input.checked) {
            if (isNestedItems) {
                const rows = data.map((item) => item.items);
                this.selectedRows = flatten(rows).filter(this.selectableResolver);
            } else {
                this.selectedRows = this.data?.data?.filter(this.selectableResolver);
            }
        } else {
            this.selectedRows = [];
        }

        this.rowSelected.emit(this.selectedRows);

        this.selectAll.emit(this.selectAllState);
    }

    expandAll() {
        this.isLoading = true;
        const newArr: unknown[] = [];
        if (this.expandDetailsByCallback) {
            this.data?.data?.forEach((val) => {
                newArr.push(this.expandDetailsByCallback(val));
            });
            this.expandedDetailKeys = newArr;
        }
        this.isAllExpanded = true;
        this.isLoading = false;

        this.onDetailExpandAll.emit();
    }

    collapseAll() {
        this.expandedDetailKeys = [];
        this.isAllExpanded = false;
    }

    toggleChange(field: string, value: any) {
        this.setArg(field, value);
    }

    isFieldToggleEnabled(field: string): boolean {
        return this.toggles.some((t) => t.field === field && t.checked);
    }

    detailExpand(event: DetailExpandEvent): void {
        this.isAllExpanded = false;
        this.onDetailExpand.emit(event);
    }

    detailCollapse(event: DetailCollapseEvent): void {
        this.isAllExpanded = false;
        this.onDetailCollapse.emit(event);
    }

    //check if there is uniqueName, which distinguish columns with the same field name
    findGridColumnState(
        item: GridColumnStateModel,
        colStates: GridColumnStateModel[] | QueryList<GridColumnDirective>
    ): GridColumnStateModel | GridColumnDirective | undefined | null {
        if (Object.prototype.hasOwnProperty.call(item, 'uniqueName')) {
            return colStates.find((i) => i.uniqueName === item.uniqueName && i.field === item.field) ?? undefined;
        } else {
            return colStates.find((i) => i.field === item.field) ?? undefined;
        }
    }

    get selectAllState(): 'unchecked' | 'checked' | 'indeterminate' {
        if (!this.data || !this.data.data || !this.data.data.filter) {
            return 'unchecked';
        }

        let selectableItems = this.data.data;

        if (selectableItems.length && (selectableItems[0] as GroupResult).items) {
            const rows = (selectableItems as GroupResult[]).map((group) => group.items);
            selectableItems = flatten(rows);
        }

        if (this.selectableResolver) {
            selectableItems = selectableItems.filter(this.selectableResolver);
        }

        if (this.selectedRows.length) {
            if (this.selectedRows.length === selectableItems.length) {
                return 'checked';
            } else {
                return 'indeterminate';
            }
        } else {
            return 'unchecked';
        }
    }

    private _loadToggleState(): void {
        const savedArgs = this.gridService.getFetchArgs(this.getName());
        const args = savedArgs ? cloneDeep(savedArgs) : cloneDeep(this.defaultFetchArgs);

        for (const toggle of this.toggles) {
            if (!toggle.value) {
                continue;
            }

            if (!savedArgs) {
                if (toggle.checked) {
                    args.filter[toggle.field] = toggle.value;
                }
                continue;
            }

            const filterValue = args.filter[toggle.field];

            if (isEqual(filterValue, toggle.value)) {
                toggle.checked = true;
                args.filter[toggle.field] = toggle.value;
            } else {
                toggle.checked = false;
            }
        }

        this.setArgs(args);
        this.filterService?.filter(this.filter);
    }

    private _setHeaderCheckboxState(indeterminate: boolean, checked: boolean): void {
        const headerCheckbox = this._document.getElementById(this.headerCheckboxSelector()) as HTMLInputElement;

        if (headerCheckbox) {
            headerCheckbox.indeterminate = indeterminate;
            headerCheckbox.checked = checked;
        }
    }

    getName(): string {
        if (this.name?.length && this.name.length > 0) {
            return this.name;
        }
        // AN-20790: If no name supplied, get closest tag starting with nova- to avoid name collision
        // No need to get this every time, store it after getting it
        this.name = this.getParentTag(this.el.nativeElement, 0);
        return this.name;
    }

    private getParentTag(el: any, count: number): string {
        if (!el) {
            return '';
        }

        if (!el.parentNode || !el.parentNode.tagName) {
            return `${el.tagName.toLowerCase()}-grid`;
        }

        const tagName = el.parentNode.tagName.toLowerCase();

        // only search set parents up to avoid infinite loop if not found
        if (tagName.startsWith('nova-') || count > 3) {
            return `${tagName}-grid`;
        } else {
            return this.getParentTag(el.parentNode, count + 1);
        }
    }

    //HOT-2214: AlexP: Without forcing even when "value == this._args" - "this.filter" object can be old and needs refresh (from def grid filter state for ex).
    private setArgs(value: FetchArgs, force = false): void {
        if (value && (value !== this._args || force) && value.pageSize < this.maxRecords) {
            if (this.dataState !== 'display') {
                value.pageSize = this._args.pageSize;
            }
            this._args = cloneDeep(value);
            if (value.filter) {
                // The issue with the mutability of filter object.
                // We should make a copy of existing filters
                // and push new instance for triggering changes event in grid.
                const filter = cloneDeep(this.filter);
                // TODO: Make a clean function and return a new filter instance (to avoid mutability).
                objectToFilters(filter, value.filter);

                this.filter = filter;
            }
            if (value.sort) {
                this.sort = [{ field: value.sort.field, dir: value.sort.dir === 'asc' ? 'asc' : 'desc' }];
            } else {
                this.sort = [];
            }
        }
    }

    private setArg(field: string, value: any) {
        this.args.filter = Object.assign({}, this.args.filter, { [field]: value });

        const filter = cloneDeep(this.filter);
        objectToFilters(filter, this._args.filter);
        this.filter = filter;

        this.filterService?.changes.next(this.filter);
    }

    getColumnState(): GridColumnStateModel[] {
        return this.columns.map((col) => {
            // Only access title if col is a GridColumnDirective
            if ('title' in col) {
                const colDirective = col as GridColumnDirective;
                const underlyingCol = this.grid.columns.find((c) => c.title === colDirective.title);
                return {
                    field: colDirective.field ?? '',
                    width: underlyingCol?.width ?? 0,
                    order: underlyingCol?.orderIndex ?? 0,
                    orderIndex: underlyingCol?.orderIndex ?? 0,
                    hidden: underlyingCol?.hidden ?? false,
                    uniqueName: colDirective.uniqueName ?? ''
                };
            } else {
                // fallback for GridColumnStateModel, if needed
                return {
                    field: (col as GridColumnStateModel).field ?? '',
                    width: 0,
                    order: 0,
                    orderIndex: 0,
                    hidden: false,
                    uniqueName: (col as GridColumnStateModel).uniqueName ?? ''
                };
            }
        });
    }

    setColumnState(colStates: GridColumnStateModel[]): void {
        for (const colState of colStates) {
            let col = null;
            col = this.findGridColumnState(colState, this.columns);

            if (!col) {
                continue;
            }

            // Only access title if col is a GridColumnDirective
            if ('title' in col) {
                const underlyingCol = this.grid.columns.find((c) => c.title === col.title);

                if (underlyingCol) {
                    col.hidden = colState.hidden ?? col.hidden;
                    col.width = colState.width ?? col.width;
                    col.orderIndex = colState.orderIndex ?? col.orderIndex;
                    underlyingCol.orderIndex = colState.orderIndex ?? col.orderIndex;
                }
            }
        }
    }

    private setData(value: GridDataResult | GroupResult[]): void {
        if (!this.grid || !value) {
            return;
        }

        this.setLoadingSpinnerMode(value);

        if (this.dataState === 'display') {
            this.grid.data = value;

            if (this.resizable && this.autoFitColumns) {
                //use setTimeout() to run this in the next change detection cycle
                this.setTimeoutById('autoFitColumns', () => this.grid.autoFitColumns(), 0);
            }

            if (this.expandedDetailKeys.length) {
                //collapses all rows and then re-expands them to reload the data
                //otherwise master-detail records are gonna be mismatched
                const expandedDetailKeys = this.expandedDetailKeys;
                this.expandedDetailKeys = [];
                this.setTimeoutById(
                    'expandedDetailKeys',
                    () => {
                        this.expandedDetailKeys = expandedDetailKeys;
                        expandedDetailKeys.forEach((e) => this.expandRowData(e));
                    },
                    0
                );
            }
        } else {
            const saveData = this.grid.data;
            try {
                this.grid.data = value;
                switch (this.dataState) {
                    case 'exportToExcel':
                        this.grid.saveAsExcel();
                        break;
                    case 'exportToPdf':
                        this.grid.saveAsPDF();
                        break;
                }
            } finally {
                this.grid.data = saveData;
                this.dataState = 'display';
            }
        }
    }

    private setLoadingSpinnerMode(gridResults: GridDataResult | GroupResult[]) {
        const gridResultCountMinimum = 25;
        const gridResultCount = Array.isArray(gridResults)
            ? gridResults.reduce((sum, grp) => sum + (grp.items?.length ?? 0), 0)
            : (gridResults.data?.length ?? 0);
        this.useUnpagedLoadingSpinner = !this.pageable && gridResultCount > gridResultCountMinimum;
    }

    private hasAppropriatePermissionsForLevel(permissions: SpecialPermissions, reqLevel: DocumentTypeLevel): boolean {
        let showButtons = false;
        switch (reqLevel) {
            case DocumentTypeLevel.Global:
            case DocumentTypeLevel.State:
                if (
                    permissions.canEditTravelGlobal ||
                    permissions.canEditLocumsGlobal ||
                    permissions.canEditAccessControlGlobal
                ) {
                    showButtons = true;
                }
                break;
            case DocumentTypeLevel.Facility:
                if (
                    permissions.canEditTravelFacility ||
                    permissions.canEditLocumsFacility ||
                    permissions.canEditAccessControlFacility
                ) {
                    showButtons = true;
                }
                break;
            case DocumentTypeLevel.QmAssociation:
                if (
                    permissions.canEditTravelAssociation ||
                    permissions.canEditLocumsAssociation ||
                    permissions.canEditAccessControlAssociation
                ) {
                    showButtons = true;
                }
                break;
            default:
                break;
        }
        return showButtons;
    }

    private hasAppropriateTravelPermissionsForLevel(
        permissions: SpecialPermissions,
        reqLevel: DocumentTypeLevel
    ): boolean {
        let showButtons = false;
        switch (reqLevel) {
            case DocumentTypeLevel.Global:
            case DocumentTypeLevel.State:
                showButtons = permissions.canEditTravelGlobal;
                break;
            case DocumentTypeLevel.Facility:
                showButtons = permissions.canEditTravelFacility;
                break;
            case DocumentTypeLevel.QmAssociation:
                showButtons = permissions.canEditTravelAssociation;
                break;
            default:
                break;
        }
        return showButtons;
    }

    private hasAppropriateLocumsPermissionsForLevel(
        permissions: SpecialPermissions,
        reqLevel: DocumentTypeLevel
    ): boolean {
        let showButtons = false;
        switch (reqLevel) {
            case DocumentTypeLevel.Global:
            case DocumentTypeLevel.State:
                showButtons = permissions.canEditLocumsGlobal;
                break;
            case DocumentTypeLevel.Facility:
                showButtons = permissions.canEditLocumsFacility;
                break;
            case DocumentTypeLevel.QmAssociation:
                showButtons = permissions.canEditLocumsAssociation;
                break;
            default:
                break;
        }
        return showButtons;
    }

    private hasAppropriateAccessControlPermissionsForLevel(
        permissions: SpecialPermissions,
        reqLevel: DocumentTypeLevel
    ): boolean {
        let showButtons = false;
        switch (reqLevel) {
            case DocumentTypeLevel.Global:
            case DocumentTypeLevel.State:
                showButtons = permissions.canEditAccessControlGlobal;
                break;
            case DocumentTypeLevel.Facility:
                showButtons = permissions.canEditAccessControlFacility;
                break;
            case DocumentTypeLevel.QmAssociation:
                showButtons = permissions.canEditAccessControlAssociation;
                break;
            default:
                break;
        }
        return showButtons;
    }
}
